import clsx from "clsx";
import _ from "lodash";
import { FormLabel } from "react-bootstrap";
import { type Control, type FieldPath, type FieldValues, type UseControllerProps } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select, { type ActionMeta, type OnChangeValue } from "react-select";

import { useScopedController } from "@/components/ScopedForm/hooks";
import RHErrorMessage from "@/containers/RHErrorMessage";
import TTFormGroup from "@/containers/TTFormGroup";
import { itemByValue } from "@/new_utils";

interface OptionProp extends Record<string, unknown> {
  value: string | number | boolean;
  label: string;
}

interface RHReactSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
> extends UseControllerProps<TFieldValues, TName, TTransformedValues> {
  control: Control<TFieldValues, any, TTransformedValues>;

  options: OptionProp[];
  id?: string;
  placeholder?: string;
  className?: string;
  validate?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  onChange?: (value: any, actionMeta: ActionMeta<OptionProp>) => void;
  isInvalid?: boolean;
  isValid?: boolean;
  isDisabled?: boolean;

  hideError?: boolean;
  namespace?: string;
  errorClassName?: string;
  containerClassName?: string;

  label?: string;
  labelClassName?: string;
}

export default function RHReactSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>({
  options,
  id,
  className,
  placeholder,
  validate = true,
  isMulti = false,
  isDisabled = false,
  isClearable = false,
  onChange,
  isValid,
  isInvalid,
  hideError = false,
  namespace,
  errorClassName,
  containerClassName,
  label,
  labelClassName,
  ...props
}: RHReactSelectProps<TFieldValues, TName, TTransformedValues>) {
  const { t } = useTranslation(["translation"]);

  const {
    field,
    formState: { isSubmitted },
    fieldState: { invalid, isTouched, error },
  } = useScopedController(_.omit(props, ["disabled"]));

  function handleChange(
    ev: OnChangeValue<OptionProp, true> | OnChangeValue<OptionProp, false>,
    opts: ActionMeta<OptionProp>,
  ) {
    let value;

    if (isMulti) {
      value = ((ev || []) as OptionProp[]).map((row: OptionProp) => row.value);
    } else {
      value = (ev as OptionProp)?.value;
    }

    field.onChange(value);

    if (onChange) {
      onChange(ev, opts);
    }
  }

  if (label && id) {
    return (
      <TTFormGroup className={containerClassName}>
        <FormLabel htmlFor={id} className={labelClassName}>
          {label}
        </FormLabel>

        <Select
          {...field}
          placeholder={placeholder || t("translation:global.choose…")}
          classNamePrefix="TT-RH-react-select"
          className={clsx("TT-RH-react-select", className, {
            "is-invalid": isInvalid ?? (validate && invalid && (isTouched || isSubmitted)),
            "is-valid": isValid ?? (validate && !invalid && (isTouched || isSubmitted)),
          })}
          id={id}
          options={options}
          value={itemByValue(field.value, options, isMulti) || null}
          onBlur={field.onBlur}
          onChange={handleChange}
          isClearable={isClearable}
          isDisabled={isDisabled}
          isMulti={isMulti}
        />

        {validate && !hideError && (
          <RHErrorMessage namespace={namespace} errorMessage={error} className={errorClassName} />
        )}
      </TTFormGroup>
    );
  }

  return (
    <>
      <Select
        {...field}
        placeholder={placeholder || t("translation:global.choose…")}
        classNamePrefix="TT-RH-react-select"
        className={clsx("TT-RH-react-select", className, {
          "is-invalid": isInvalid ?? (validate && invalid && isTouched),
          "is-valid": isValid ?? (validate && !invalid && isTouched),
        })}
        id={id}
        options={options}
        value={itemByValue(field.value, options, isMulti) || null}
        onBlur={field.onBlur}
        onChange={handleChange}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isMulti={isMulti}
      />

      {validate && !hideError && (
        <RHErrorMessage namespace={namespace} errorMessage={error} className={errorClassName} />
      )}
    </>
  );
}
